import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Sparkles, ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { getJobStatus, postForm } from "../lib/api";

const inputStyle = {
  width: "100%",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-strong)",
  background: "var(--surface)",
  color: "var(--text-primary)",
  padding: "10px 14px",
  fontSize: "0.875rem",
  outline: "none",
  transition: "var(--transition-base)",
};

export default function Pipeline1() {
  const nav = useNavigate();
  const pollRef = useRef(null);
  const activeJobIdRef = useRef(null);

  const [mode, setMode] = useState("auto");
  const [room, setRoom] = useState(null);
  const [roomPreview, setRoomPreview] = useState(null);
  const [roomType, setRoomType] = useState("bedroom");
  const [dimensions, setDimensions] = useState("12x10");
  const [style, setStyle] = useState("minimal");
  const [budget, setBudget] = useState("medium");
  const [furnitures, setFurnitures] = useState([""]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [statusText, setStatusText] = useState(null);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setRoom(file);
      const reader = new FileReader();
      reader.onloadend = () => setRoomPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const addFurniture = () => { if (furnitures.length < 7) setFurnitures([...furnitures, ""]); };
  const removeFurniture = (i) => { if (furnitures.length > 1) setFurnitures(furnitures.filter((_, idx) => idx !== i)); };
  const updateFurniture = (i, val) => { const n = [...furnitures]; n[i] = val; setFurnitures(n); };

  function stopPolling() {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }

  async function pollOnce(id) {
    const status = await getJobStatus(id);
    if (activeJobIdRef.current !== id) return;
    setStatusText(status.message ?? `Job status: ${status.status}`);
    if (status.status === "completed") {
      stopPolling(); setBusy(false);
      if (status.result_url) nav(`/interior-design-ai/result?url=${encodeURIComponent(status.result_url)}`);
      else setErr("Job completed but result URL was missing.");
      return;
    }
    if (status.status === "failed") { stopPolling(); setBusy(false); setErr(status.error ?? "Pipeline execution failed"); }
  }

  function startPolling(id) {
    if (!id) { setBusy(false); setErr("Missing job_id from backend response"); return; }
    activeJobIdRef.current = id; stopPolling();
    pollOnce(id).catch((e) => { stopPolling(); setBusy(false); setErr(e.message); });
    pollRef.current = window.setInterval(async () => {
      try { await pollOnce(id); } catch (e) { stopPolling(); setBusy(false); setErr(e.message); }
    }, 3000);
  }

  async function run() {
    if (!room) return;
    if (mode === "custom" && !furnitures.filter((f) => f.trim()).length) { setErr("Please add at least one furniture name."); return; }
    stopPolling(); activeJobIdRef.current = null;
    setErr(null); setBusy(true); setStatusText("Submitting job..."); setJobId(null);
    try {
      const fd = new FormData();
      fd.append("room_image", room); fd.append("room_type", roomType);
      fd.append("dimensions", dimensions); fd.append("style", style); fd.append("budget", budget);
      const endpoint = mode === "auto" ? "/run/pipeline1" : "/run/pipeline1-custom";
      if (mode === "custom") fd.append("furniture_names", furnitures.filter((f) => f.trim()).join(","));
      const data = await postForm(endpoint, fd);
      const nextId = data?.job_id ? String(data.job_id) : "";
      if (!nextId) throw new Error("Backend did not return job_id");
      setJobId(nextId); setStatusText(data?.message ?? "Job queued. Waiting for pipeline to start..."); startPolling(nextId);
    } catch (e) { stopPolling(); setErr(e.message ?? "Pipeline execution failed"); setBusy(false); setStatusText(null); }
  }

  const cardStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-sm)",
    padding: "1.25rem",
  };

  const labelStyle = { fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: 4 };

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-soft)" }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">

        {/* Back + header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 animate-fade-up">
          <div>
            <button
              onClick={() => nav("/interior-design-ai/dashboard")}
              className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 mb-3 transition-all"
              style={{ color: "var(--text-secondary)", border: "1px solid var(--border-strong)", background: "var(--surface)" }}
            >
              <ArrowLeft className="w-3 h-3" /> Dashboard
            </button>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
              {mode === "auto" ? "Auto-Furnish Pipeline" : "Custom Furniture Pipeline"}
            </h2>
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              {mode === "auto" ? "Upload your room image and let AI suggest furniture" : "Specify exactly which furniture pieces you want to see"}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="iai-tab-bar shrink-0">
            {["auto", "custom"].map((m) => (
              <button key={m} onClick={() => setMode(m)} className={`iai-tab ${mode === m ? "active" : ""}`}>
                {m === "auto" ? "Auto-Furnish" : "Custom Furniture"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left */}
          <div className="space-y-4">
            {/* Room upload */}
            <div style={cardStyle}>
              <h3 className="font-semibold flex items-center gap-2 mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
                <Upload className="w-4 h-4" style={{ color: "var(--orange)" }} /> Room Image
              </h3>
              <div
                className={`iai-upload-zone relative aspect-video flex flex-col items-center justify-center overflow-hidden ${roomPreview ? "has-file" : ""}`}
                onClick={() => document.getElementById("room-upload")?.click()}
              >
                {roomPreview ? (
                  <img src={roomPreview} className="w-full h-full object-cover" style={{ borderRadius: "var(--radius-md)" }} alt="Preview" />
                ) : (
                  <div className="text-center space-y-2 p-6">
                    <Upload className="w-8 h-8 mx-auto" style={{ color: "var(--text-muted)" }} />
                    <p className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>Click to upload room image</p>
                    <p className="text-xs" style={{ color: "var(--text-muted)" }}>PNG, JPG up to 10MB</p>
                  </div>
                )}
                <input id="room-upload" type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </div>
            </div>

            {/* Custom furniture */}
            {mode === "custom" && (
              <div style={cardStyle}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
                    <Plus className="w-4 h-4" style={{ color: "var(--orange)" }} /> Furniture Names
                  </h3>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>{furnitures.length}/7</span>
                </div>
                <div className="space-y-2">
                  {furnitures.map((f, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        className="input-premium flex-1"
                        style={inputStyle}
                        value={f}
                        onChange={(e) => updateFurniture(i, e.target.value)}
                        placeholder={`Furniture ${i + 1} (e.g. Sofa)`}
                      />
                      {furnitures.length > 1 && (
                        <button
                          onClick={() => removeFurniture(i)}
                          className="p-2 rounded-lg transition-colors"
                          style={{ color: "var(--text-muted)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {furnitures.length < 7 && (
                  <button
                    onClick={addFurniture}
                    className="w-full mt-3 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all"
                    style={{ border: "2px dashed var(--border-strong)", color: "var(--text-muted)", background: "transparent" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--orange)"; e.currentTarget.style.color = "var(--orange)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.color = "var(--text-muted)"; }}
                  >
                    <Plus className="w-4 h-4" /> Add Another
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Right: Design parameters */}
          <div style={cardStyle}>
            <h3 className="font-semibold flex items-center gap-2 mb-5" style={{ fontFamily: "'Outfit', sans-serif", color: "var(--text-primary)" }}>
              <Sparkles className="w-4 h-4" style={{ color: "var(--orange)" }} /> Design Parameters
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label: "Room Type", id: "roomType", val: roomType, set: setRoomType, opts: [["bedroom","Bedroom"],["living room","Living Room"],["dining room","Dining Room"],["office","Office"]] },
                { label: "Style", id: "style", val: style, set: setStyle, opts: [["minimal","Minimal"],["modern","Modern"],["bohemian","Bohemian"],["luxury","Luxury"]] },
                { label: "Budget", id: "budget", val: budget, set: setBudget, opts: [["low","Low"],["medium","Medium"],["high","High"]] },
              ].map(({ label, id, val, set, opts }) => (
                <div key={id}>
                  <label style={labelStyle}>{label}</label>
                  <select className="input-premium" style={inputStyle} value={val} onChange={(e) => set(e.target.value)}>
                    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
              <div>
                <label style={labelStyle}>Dimensions</label>
                <input className="input-premium" style={inputStyle} value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder="e.g. 12x10" />
              </div>
            </div>

            {jobId && <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Job ID: {jobId}</p>}
            {statusText && <div className="iai-status-info mb-3">{statusText}</div>}
            {err && <div className="iai-status-error mb-3">{err}</div>}

            <button
              onClick={run}
              disabled={!room || busy}
              className="btn-primary-glow w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, var(--orange), var(--orange-light))" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Processing..." : "Generate Result"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
